import { ArrowDown, ArrowUp, Plus, Save, Trash2 } from "lucide-react";
import { Link } from "@/lib/nav";
import { useRouter } from "@/lib/nav";
import { ReactNode, useMemo, useState, useTransition } from "react";
import { toast } from "sonner";

import Field from "@/components/field";
import Money from "@/components/money";
import { UNSAVED_LEAVE_CONFIRM } from "@/components/save-bar";
import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import { Input } from "@buildpy/ui/input";
import { Textarea } from "@buildpy/ui/textarea";
import { useLeaveGuard } from "@/hooks/use-leave-guard";
import { formatNumber } from "@buildpy/core/lib/format";
import { settleRedirect } from "@/lib/redirect-error";
import { cn } from "@/lib/utils";
import { saveContractDraft } from "@/api/actions/contract";
import { contractVatModeOf } from "@buildpy/core/modules/change-order/calc";
import { splitPaymentAmounts, usesDelayPenalty } from "@buildpy/core/modules/contract/calc";
import { totalVatNote } from "@buildpy/core/modules/quote/snapshot-vat";
import { ContractEditInput } from "@buildpy/core/modules/contract/schema";
import {
  bpToRate,
  paymentPlanIssues,
  RATE_FIELDS,
  RATE_UNIT_SUFFIX,
  rateHintOf,
  rateToBp,
  SettingsIssue,
} from "@buildpy/core/modules/contract/settings";
import { ClauseSource, SpecialTerm } from "@buildpy/core/modules/contract/types";

export interface EditorPayment {
  label: string;
  ratio_bp: number;
  trigger_text: string;
  trigger_phase_id: string | null;
  due_date: string | null;
  note: string;
}

export type EditorRateKey = "delay_rate_bp" | "delay_cap_bp" | "late_interest_rate_bp";

interface Props {
  contractId: string;
  contractNo: string;
  baseRev: number;
  clauseSource: ClauseSource;
  // 계약금액(부가세 포함 총액) — 견적에서 온 값이라 여기서 고치지 않는다.
  totalAmount: number;
  vatIncluded: boolean;
  startDate: string | null;
  endDate: string | null;
  payments: EditorPayment[];
  rates: Record<EditorRateKey, number>;
  specialTerms: SpecialTerm[];
}

// 계약 편집은 이 세 요율만 — 위약금·하자보증금은 설정 기본값을 그대로 쓴다.
const EDIT_RATES = RATE_FIELDS.filter(
  (f): f is (typeof RATE_FIELDS)[number] & { key: EditorRateKey } =>
    f.key === "delay_rate_bp" || f.key === "delay_cap_bp" || f.key === "late_interest_rate_bp"
);

interface PaymentDraft extends Omit<EditorPayment, "ratio_bp"> {
  ratio: string;
}

const move = <T,>(rows: T[], index: number, delta: number): T[] => {
  const next = index + delta;
  if (next < 0 || next >= rows.length) return rows;
  const copy = rows.slice();
  [copy[index], copy[next]] = [copy[next], copy[index]];
  return copy;
};

// 계약 초안 편집기 — 입력 모양은 schema.ts, 규칙(비율 합·날짜 순서·특약)은 저장소가 한 번 더 본다.
const ContractEditor = (props: Props) => {
  const router = useRouter();
  const [pending, startTransition] = useTransition();
  const [rev, setRev] = useState(props.baseRev);
  const [serverError, setServerError] = useState<{ message: string; locked: boolean } | null>(null);

  const [startDate, setStartDate] = useState(props.startDate ?? "");
  const [endDate, setEndDate] = useState(props.endDate ?? "");
  const [payments, setPayments] = useState<PaymentDraft[]>(() =>
    props.payments.map(({ ratio_bp, ...rest }) => ({ ...rest, ratio: String(bpToRate(ratio_bp, "percent")) }))
  );
  const [rates, setRates] = useState<Record<EditorRateKey, string>>(() => ({
    delay_rate_bp: String(bpToRate(props.rates.delay_rate_bp, "permille")),
    delay_cap_bp: String(bpToRate(props.rates.delay_cap_bp, "percent")),
    late_interest_rate_bp: String(bpToRate(props.rates.late_interest_rate_bp, "percent")),
  }));
  const [terms, setTerms] = useState<SpecialTerm[]>(props.specialTerms);
  // 화면 글자 그대로 견준다 — 저장한 값과 다르면 떠날 때(취소·메뉴·뒤로·새로고침) 묻는다.
  const snapshot = JSON.stringify({ startDate, endDate, payments, rates, terms });
  const [savedSnapshot, setSavedSnapshot] = useState(snapshot);
  const dirty = snapshot !== savedSnapshot;
  useLeaveGuard({ unload: dirty, links: dirty, back: dirty, message: UNSAVED_LEAVE_CONFIRM });

  const delayShown = usesDelayPenalty(props.clauseSource);
  const shownRates = useMemo(() => EDIT_RATES.filter((f) => delayShown || !f.delayOnly), [delayShown]);

  // 화면 글자 → 저장 입력. 숫자로 못 읽은 칸은 그 칸 이름으로 이슈를 남긴다.
  const { input, issues, preview } = useMemo(() => {
    const parseIssues: SettingsIssue[] = [];
    const rows = payments.map((p, i) => {
      const parsed = rateToBp(p.ratio, "percent");
      if (!parsed.ok) {
        parseIssues.push({ field: `payments.${i}.ratio`, message: `${i + 1}회차 비율 — ${parsed.message}` });
      }
      return { ...p, ratio_bp: parsed.ok ? parsed.bp : 0 };
    });
    const rateBp: Partial<Record<EditorRateKey, number>> = {};
    for (const field of shownRates) {
      const parsed = rateToBp(rates[field.key], field.unit);
      if (!parsed.ok) {
        parseIssues.push({ field: `rates.${field.key}`, message: `${field.label} — ${parsed.message}` });
      }
      rateBp[field.key] = parsed.ok ? parsed.bp : props.rates[field.key];
    }
    const next: ContractEditInput = {
      start_date: startDate || null,
      end_date: endDate || null,
      payments: rows.map((r) => ({
        label: r.label,
        ratio_bp: r.ratio_bp,
        trigger_text: r.trigger_text,
        trigger_phase_id: r.trigger_phase_id,
        due_date: r.due_date,
        note: r.note,
      })),
      ...(delayShown ? { delay_rate_bp: rateBp.delay_rate_bp, delay_cap_bp: rateBp.delay_cap_bp } : {}),
      late_interest_rate_bp: rateBp.late_interest_rate_bp ?? props.rates.late_interest_rate_bp,
      special_terms: terms.map((t) => ({ title: t.title, body: t.body })),
    };
    const planIssues = paymentPlanIssues(rows.map((r, i) => ({ ...r, seq: i + 1 })));
    const all = [...parseIssues, ...planIssues];
    if (startDate && endDate && endDate < startDate) {
      all.push({ field: "dates", message: "준공일이 착공일보다 앞설 수 없습니다" });
    }
    const ok = !all.some((issue) => issue.field.startsWith("payments."));
    return {
      input: next,
      issues: all,
      preview: ok ? splitPaymentAmounts(props.totalAmount, rows.map((r) => r.ratio_bp)) : null,
    };
  }, [payments, rates, startDate, endDate, terms, delayShown, shownRates, props.rates, props.totalAmount]);

  const fieldHas = (field: string) => issues.some((issue) => issue.field === field);
  const issuesIn = (section: string) => issues.filter((issue) => issue.field.split(".")[0] === section);
  const ratioSum = input.payments.reduce((sum, p) => sum + p.ratio_bp, 0);
  const previewSum = preview?.reduce((a, b) => a + b, 0) ?? null;

  const save = () => {
    setServerError(null);
    const sent = snapshot;
    startTransition(async () => {
      try {
        const result = await saveContractDraft(props.contractId, input, rev);
        if (!result.ok) {
          setServerError({ message: result.message, locked: result.locked === true });
          toast.error(result.message);
          return;
        }
        setRev(result.rev);
        setSavedSnapshot(sent);
        toast.success("저장했습니다");
        router.push(`/app/contracts/${props.contractId}`);
      } catch (error) {
        if (settleRedirect(error)) return;
        toast.error("저장하지 못했습니다");
      }
    });
  };

  return (
    <div className="flex flex-col gap-y-4">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div className="flex flex-col gap-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">계약 {props.contractNo} 편집</h1>
          <p className="text-sm text-muted-foreground" data-testid="contract-total">
            계약금액 <Money value={props.totalAmount} /> ({totalVatNote(contractVatModeOf({ vat_included: props.vatIncluded }))}) — 견적에서
            정해진 금액이라 여기서 고치지 않습니다.
          </p>
        </div>
        <div className="flex items-center gap-x-2">
          <Button asChild size="sm" variant="ghost">
            <Link href={`/app/contracts/${props.contractId}`}>취소</Link>
          </Button>
          <Button disabled={pending || issues.length > 0} onClick={save} size="sm" type="button" variant="primary">
            <Save />
            저장
          </Button>
        </div>
      </div>

      {serverError && (
        <p className="rounded-md border border-destructive px-3 py-2 text-sm text-destructive" role="alert">
          {serverError.message}
          {serverError.locked && (
            <Link className="ml-2 underline" href={`/app/contracts/${props.contractId}`}>
              상세로 돌아가기
            </Link>
          )}
        </p>
      )}

      <Section issues={issuesIn("dates")} title="공사 기간">
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="착공일">
            <Input inputSize="sm" name="start_date" onChange={(e) => setStartDate(e.target.value)} type="date" value={startDate} />
          </Field>
          <Field label="준공일">
            <Input inputSize="sm" name="end_date" onChange={(e) => setEndDate(e.target.value)} type="date" value={endDate} />
          </Field>
        </div>
      </Section>

      <Section
        hint="비율의 합이 100% 여야 하고, 지급 시기는 꼭 적습니다. 끝수는 마지막 회차가 받습니다."
        issues={issuesIn("payments")}
        title="지급회차">
        <div className={cn("hidden items-center gap-2 text-xs text-muted-foreground lg:grid", PAY_TRACKS)}>
          <span>회차 이름</span>
          <span>비율 (%)</span>
          <span>지급 시기</span>
          <span className="text-right">금액</span>
          <span />
        </div>
        {payments.map((row, i) => (
          <div key={i} className={cn(PAY_ROW, PAY_TRACKS)}>
            <Field className={PAY_CELL} label="회차 이름">
              <Input
                aria-invalid={fieldHas(`payments.${i}.label`)}
                aria-label={`${i + 1}회차 이름`}
                className="min-w-0"
                inputSize="sm"
                name={`payments.${i}.label`}
                onChange={(e) => setPayments((prev) => prev.map((p, j) => (j === i ? { ...p, label: e.target.value } : p)))}
                value={row.label}
              />
            </Field>
            <Field className={PAY_CELL} label="비율 (%)">
              <Input
                aria-invalid={fieldHas(`payments.${i}.ratio`)}
                aria-label={`${i + 1}회차 비율`}
                inputSize="sm"
                inputMode="decimal"
                name={`payments.${i}.ratio`}
                onChange={(e) => setPayments((prev) => prev.map((p, j) => (j === i ? { ...p, ratio: e.target.value } : p)))}
                value={row.ratio}
              />
            </Field>
            <Field className={cn(PAY_CELL, "col-span-2 lg:col-span-1")} label="지급 시기">
              <Input
                aria-invalid={fieldHas(`payments.${i}.trigger_text`)}
                aria-label={`${i + 1}회차 지급 시기`}
                inputSize="sm"
                name={`payments.${i}.trigger_text`}
                onChange={(e) =>
                  setPayments((prev) => prev.map((p, j) => (j === i ? { ...p, trigger_text: e.target.value } : p)))
                }
                placeholder="예: 도기세팅 또는 타일 완료"
                value={row.trigger_text}
              />
            </Field>
            <div className="flex min-w-0 flex-col gap-y-1 text-right">
              <span className="text-xs text-muted-foreground lg:hidden">금액</span>
              <span className="flex min-h-9 items-center justify-end whitespace-nowrap text-sm tabular-nums xs:text-xs">
                {preview ? <Money value={preview[i]} /> : "—"}
              </span>
            </div>
            <RowButtons
              label={`${i + 1}회차`}
              onDown={() => setPayments((prev) => move(prev, i, 1))}
              onRemove={() => setPayments((prev) => prev.filter((_, j) => j !== i))}
              onUp={() => setPayments((prev) => move(prev, i, -1))}
            />
          </div>
        ))}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <Button
            onClick={() =>
              setPayments((prev) => [
                ...prev,
                { label: "", ratio: "0", trigger_text: "", trigger_phase_id: null, due_date: null, note: "" },
              ])
            }
            size="sm"
            type="button"
            variant="outline">
            <Plus className="mr-1 size-3.5" />
            회차 추가
          </Button>
          <span
            className={cn("text-sm", fieldHas("payments.sum") ? "text-destructive" : "text-muted-foreground")}
            data-testid="payment-sum">
            합계 {formatNumber(bpToRate(ratioSum, "percent"))}% ·{" "}
            {previewSum === null ? "금액 —" : <Money value={previewSum} />} / 계약금액 <Money value={props.totalAmount} />
          </span>
        </div>
      </Section>

      <Section issues={issuesIn("rates")} title="약정 수치">
        {!delayShown && (
          <p className="rounded-md bg-muted/50 p-3 text-sm" data-testid="ftc-delay-notice">
            공정위 서식에는 지체상금 조항이 없습니다 — 대신 지연이자율(제6조)이 쓰입니다.
          </p>
        )}
        <div className="grid gap-4 md:grid-cols-2">
          {shownRates.map((f) => (
            <Field key={f.key} label={`${f.label} (${RATE_UNIT_SUFFIX[f.unit]})`}>
              <Input
                aria-invalid={fieldHas(`rates.${f.key}`)}
                aria-label={f.label}
                inputSize="sm"
                inputMode="decimal"
                name={`rates.${f.key}`}
                onChange={(e) => setRates((prev) => ({ ...prev, [f.key]: e.target.value }))}
                value={rates[f.key]}
              />
              <span className="text-xs text-muted-foreground">{rateHintOf(f, props.clauseSource)}</span>
            </Field>
          ))}
        </div>
      </Section>

      <Section
        hint="원문이 다루지 않는 약속은 조항을 고치지 말고 특약으로 적습니다."
        issues={issuesIn("special_terms")}
        title="특약">
        {terms.length === 0 && <p className="text-sm text-muted-foreground">특약이 없습니다.</p>}
        {terms.map((term, i) => (
          <div key={i} className="flex flex-col gap-y-2 rounded-md border p-3">
            <div className="flex items-center gap-x-2">
              <span className="w-8 shrink-0 text-sm text-muted-foreground">{i + 1}.</span>
              <Input
                aria-label={`특약 ${i + 1} 제목`}
                inputSize="sm"
                name={`special_terms.${i}.title`}
                onChange={(e) => setTerms((prev) => prev.map((t, j) => (j === i ? { ...t, title: e.target.value } : t)))}
                placeholder="제목"
                value={term.title}
              />
              <RowButtons
                label={`특약 ${i + 1}`}
                onDown={() => setTerms((prev) => move(prev, i, 1))}
                onRemove={() => setTerms((prev) => prev.filter((_, j) => j !== i))}
                onUp={() => setTerms((prev) => move(prev, i, -1))}
              />
            </div>
            <Textarea
              aria-label={`특약 ${i + 1} 내용`}
              className="min-h-20 text-sm"
              onChange={(e) => setTerms((prev) => prev.map((t, j) => (j === i ? { ...t, body: e.target.value } : t)))}
              placeholder="내용"
              value={term.body}
            />
          </div>
        ))}
        <div>
          <Button
            onClick={() => setTerms((prev) => [...prev, { no: prev.length + 1, title: "", body: "" }])}
            size="sm"
            type="button"
            variant="outline">
            <Plus className="mr-1 size-3.5" />
            특약 추가
          </Button>
        </div>
      </Section>
    </div>
  );
};

export default ContractEditor;

const Section = ({
  title,
  hint,
  issues,
  children,
}: {
  title: string;
  hint?: string;
  issues: SettingsIssue[];
  children: ReactNode;
}) => (
  <SectionCard description={hint} title={title}>
    <div className="flex flex-col gap-y-3">
      {issues.length > 0 && (
        <ul className="flex flex-col gap-y-0.5 text-sm text-destructive" role="alert">
          {issues.map((issue, i) => (
            <li key={`${issue.field}-${i}`}>{issue.message}</li>
          ))}
        </ul>
      )}
      {children}
    </div>
  </SectionCard>
);

// 지급회차 — lg 부터 머리줄과 같은 트랙(금액 8rem = 「9,999,999,999원」 · 끝 7.5rem = RowButtons 의 sm 버튼 3개), md~lg 는 이름·비율·금액(8rem) / 지급 시기·버튼 두 줄 카드, md 미만은 이름·비율 / 지급 시기 / 금액·버튼 세 줄(xs 는 카드 패딩 p-1 로 금액 칸 116px — text-xs 12px 의 99억대 금액이 든다).
const PAY_TRACKS = "lg:grid-cols-[6rem_6rem_minmax(0,1fr)_8rem_7.5rem]";
const PAY_ROW =
  "grid grid-flow-row-dense grid-cols-[minmax(0,1fr)_7.5rem] items-end gap-2 rounded-md border p-3 xs:p-1 md:grid-cols-[minmax(0,1fr)_4.5rem_8rem] lg:items-center lg:border-0 lg:p-0";
const PAY_CELL = "min-w-0 lg:[&>span:first-child]:hidden";

const RowButtons = ({
  label,
  onUp,
  onDown,
  onRemove,
}: {
  label: string;
  onUp: () => void;
  onDown: () => void;
  onRemove: () => void;
}) => (
  <div className="flex shrink-0 items-center">
    <Button aria-label={`${label} 위로`} onClick={onUp} size="sm" type="button" variant="ghost">
      <ArrowUp className="size-4" />
    </Button>
    <Button aria-label={`${label} 아래로`} onClick={onDown} size="sm" type="button" variant="ghost">
      <ArrowDown className="size-4" />
    </Button>
    <Button aria-label={`${label} 삭제`} onClick={onRemove} size="sm" type="button" variant="ghost">
      <Trash2 className="size-4" />
    </Button>
  </div>
);
